import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useClickOutside } from '@/hooks/useClickOutside'
import { cn } from '@/libs/tw'

import SvgIcon from '../svg-icon/svgIcon'
import { Typography } from '../typography/typography'

type PopupProps = {
	element?: HTMLElement | null
	title: string
	content: JSX.Element
	open: boolean
	closeOnClickOutside?: boolean
	onClose?: () => void
}

function offset(el: HTMLElement) {
	const rect = el.getBoundingClientRect(),
		scrollLeft = window.scrollX || document.documentElement.scrollLeft,
		scrollTop = window.scrollY || document.documentElement.scrollTop
	return { top: rect.top + scrollTop, left: rect.left + scrollLeft, width: rect.width }
}

export const Popup = ({
	element,
	content,
	title,
	open,
	onClose,
	closeOnClickOutside,
}: PopupProps) => {
	const popUpRef = useClickOutside<HTMLDivElement>(() => closeOnClickOutside && onClose?.())
	const [position, setPosition] = useState<{ top: string; left: string }>()
	const [arrowPosition, setArrowPosition] = useState<{ top: number; left: number }>()

	useEffect(() => {
		setTimeout(() => {
			if (!element) return

			const popUpHight = popUpRef.current?.getBoundingClientRect().height ?? 0
			const popUpWidth = popUpRef.current?.getBoundingClientRect().width ?? 0
			const dayPosition = offset(element)

			setArrowPosition({
				top: dayPosition.top - 25,
				left: dayPosition.left - 30 + dayPosition.width / 2,
			})

			const left = dayPosition.left - popUpWidth / 2 + dayPosition.width / 2

			if (left < 0) {
				setPosition({
					top: `${dayPosition.top - popUpHight - 20}px`,
					left: `${0}px`,
				})

				return
			}

			const right = left + popUpWidth
			if (right > document.body.clientWidth) {
				const diff = right - document.body.clientWidth

				setPosition({
					top: `${dayPosition.top - popUpHight - 20}px`,
					left: `${left - diff}px`,
				})
				return
			}

			setPosition({
				top: `${dayPosition.top - popUpHight - 20}px`,
				left: `${left}px`,
			})
		}, 50)
	}, [element, popUpRef])

	if (!open) return null

	return createPortal(
		<>
			<div
				data-testid="popup"
				ref={popUpRef}
				className={cn(
					'absolute z-20 flex w-[342px] flex-col gap-24 rounded-8 bg-popup-bg p-24 pt-16 text-popup-text'
				)}
				style={{ top: position?.top, left: position?.left }}
			>
				<div className={cn('flex justify-between')}>
					<Typography element="p" style="p-bold">
						{title}
					</Typography>
					{onClose && (
						<button onClick={onClose}>
							<SvgIcon iconName="cross" className={cn('h-12 w-12')} />
						</button>
					)}
				</div>
				{content}
			</div>
			{arrowPosition && (
				<div
					className={cn(
						'absolute bottom-[-20px] h-0 w-0 border-l-[30px] border-r-[30px] border-t-[25px] border-popup-bg border-l-transparent border-r-transparent bg-transparent '
					)}
					style={{ left: arrowPosition?.left, top: arrowPosition?.top }}
				/>
			)}
		</>,
		document.body
	)
}
